import { BehaviorSubject, combineLatest, Observable, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, map, switchMap, takeUntil } from 'rxjs/operators';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { PowerForecastService } from '../../services/data-services/monitor/power-forecast/power-forecast.service';
import { ForecastProvidersEnum } from 'app/shared/services/data-services/graphql';
import { PowerForecast } from '../../../models/monitor/power-forecast.model';
import { EThemeName, ThemeService } from '@wind-log_library/fe-component-library';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { NavigationCategory, NavigationService, NavigationDetails } from '../../services/data-services/navigation/navigation.service';
import { MonitoringWeatherData } from '../monitoring-weather/monitoring-weather.component';
import { isEqual } from 'lodash';

@Component({
    selector: 'app-power-forecast',
    templateUrl: './power-forecast.component.html',
    styleUrls: ['./power-forecast.component.scss'],
    standalone: false
})
export class PowerForecastComponent implements OnInit, OnDestroy, OnChanges {

  @Input() public page: string;
  @Input() public forecastAvailables;
  @Input() public graphParams;

  @Output() changeParams = new EventEmitter();

  public weatherData: MonitoringWeatherData[];
  public unitsColumnStyle = {};
  public firstColumnStyle = {};
  public lastColumnStyle = {};
  public reload = new BehaviorSubject(true);
  public activeButton: number;
  public contentButtons: string[];
  public theme$: Observable<EThemeName>;
  public powerForecast: PowerForecast;
  public actualValue: string;
  public forecastOptions: string[];
  private unsubscriber = new ReplaySubject();
  private unsubscriber2 = new ReplaySubject();
  private chartHours = new BehaviorSubject<number>(48);
  private provider$ = new BehaviorSubject<ForecastProvidersEnum[]>([ForecastProvidersEnum.Windlog]);

  constructor(
    private route: ActivatedRoute,
    private powerForecastService: PowerForecastService,
    private themeService: ThemeService,
    private translate: TranslateService,
    private navigationService: NavigationService,
  ) { }

  ngOnInit() {
    this.chartHours.next(this.graphParams.hours);
    this.provider$.next(this.graphParams.selection)

    this.theme$ = this.themeService.getTheme();

    if (this.page === 'portfolio-monitor') {
      combineLatest([
        this.navigationService.getNavigationObservable([NavigationCategory.PortfolioLevelSelectedPortfolio, NavigationCategory.PortfolioLevelSelectedParks]),
        this.provider$
      ]).pipe(
        takeUntil(this.unsubscriber),
        switchMap(([navigationDetails, provider]: [NavigationDetails, ForecastProvidersEnum[]]) => {
          return this.powerForecastService.getPowerForecastPortfolioData(this.chartHours, provider, navigationDetails).pipe(
            takeUntil(this.unsubscriber),
            distinctUntilChanged((prev, curr) => isEqual(prev, curr))
          );
        })
      ).subscribe((p: PowerForecast) => {
        this.powerForecast = p;
      });
    }

    if (this.page === 'park-monitor') {
      const parkNameObservable = this.route.params.pipe(map(p => p.id));

      combineLatest([
        this.navigationService.getNavigationObservable([NavigationCategory.ParkLevelSelectedPark, NavigationCategory.ParkLevelSelectedTurbines]),
        this.provider$
      ])
      .pipe(takeUntil(this.unsubscriber), distinctUntilChanged(),)
      .subscribe(([navigationDetails, provider]: [NavigationDetails, ForecastProvidersEnum[]]) => {
        this.unsubscriber2.next(true);
        this.unsubscriber2.complete();
        this.unsubscriber2 = new ReplaySubject();
        combineLatest([
          this.powerForecastService.getPowerForecastParkData(this.chartHours, parkNameObservable, provider, navigationDetails.selectedTurbineIds).pipe(takeUntil(this.unsubscriber2), distinctUntilChanged((prev, curr) => isEqual(prev, curr))),
          this.powerForecastService.getIconForecastParkData(this.chartHours, parkNameObservable, this.weatherData?.length, navigationDetails.selectedTurbineIds).pipe(takeUntil(this.unsubscriber2), distinctUntilChanged((prev, curr) => isEqual(prev, curr))),
        ])
        .pipe(takeUntil(this.unsubscriber2))
        .subscribe(([p, icons]: [PowerForecast, { icon: string, timestamp: number }[]]) => {
          this.powerForecast = p;
          this.calcStyles();
          this.weatherData = p?.weather?.reduce((tot: MonitoringWeatherData[], weather, i: number) => {
            if (i < icons.length) {
              tot.push({ ...weather, ...icons[i] });
            }
            return tot;
          }, []);
        });
      });
    }

    this.subscribeTranslateService();
    this.generateButtons();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.forecastAvailables) {
      this.forecastOptions = this.forecastAvailables.map(item => item.option);
      if (this.forecastOptions.indexOf(this.actualValue) === -1) {
        this.actualValue = this.forecastOptions[0];
        this.provider$.next(this.forecastAvailables[0].value);
      }
    }
  }

  ngOnDestroy() {
    this.unsubscriber.next(true);
    this.unsubscriber.complete();
    this.unsubscriber2.next(true);
    this.unsubscriber2.complete();
  }

  private generateButtons() {
    const buttonName = this.graphParams.hours === 48 ? 'COMMON.PLUS_48H' : 'COMMON.PLUS_5D';
    this.contentButtons = ['COMMON.PLUS_48H', 'COMMON.PLUS_5D'];
    this.activeButton = this.contentButtons.indexOf(buttonName);
  }

  private calcStyles() {
    this.unitsColumnStyle = {'margin-left': '3px' };
    this.firstColumnStyle = {'margin-left': '-7px' };
    this.lastColumnStyle = {'margin-right': '5px' };
  }

  public handleActiveChanged(active: string) {
    const hours = active ? 120 : 48;
    this.chartHours.next(hours);
    this.changeParams.emit({hours: this.chartHours.getValue(), selection: this.provider$.getValue()});
  }

  public handleProviderChanged(active: any) {
    const selection = this.forecastAvailables[active.index]?.value;
    this.actualValue = this.forecastOptions[active.index];
    this.changeParams.emit({hours: this.chartHours.getValue(), selection});
    this.provider$.next(selection);
  }

  public rotate(value: number) {
    return { transform: 'rotate(' + (+value + 180) + 'deg)' };
  }

  private subscribeTranslateService() {
    this.translate.onLangChange.pipe(takeUntil(this.unsubscriber)).subscribe((event: LangChangeEvent) => {
      this.generateButtons();
    });
  }
}
